import { Button, EmptyState, Spinner } from "@cntrl/ui";
import { useFeatures } from "../../app/hooks/useAccount.js";
import styles from "./SettingsScreen.module.scss";

/**
 * Features tab — the guides accordion + the roadmap list (status badges) + coming-soon slots, all from
 * `GET /settings/features`. The endpoint ships EMPTY at go-live (born-blank), so the all-empty case renders a
 * designed empty state, never a bare surface.
 */
export function SettingsFeaturesTab() {
  const features = useFeatures();

  if (features.isLoading) {
    return (
      <div className={styles.center} role="status" aria-live="polite">
        <Spinner size={20} label="Loading features…" />
      </div>
    );
  }
  if (features.isError) {
    return (
      <EmptyState
        title="Couldn't load features"
        description={features.error instanceof Error ? features.error.message : "Please try again."}
        action={<Button onClick={() => void features.refetch()}>Retry</Button>}
      />
    );
  }

  const guides = features.data?.guides ?? [];
  const roadmap = features.data?.roadmap ?? [];
  const comingSoon = features.data?.comingSoon ?? [];
  const allEmpty = guides.length === 0 && roadmap.length === 0 && comingSoon.length === 0;

  if (allEmpty) {
    return (
      <EmptyState
        title="Nothing here yet"
        description="Guides, the product roadmap, and what's coming next will appear here."
      />
    );
  }

  return (
    <div className={styles.stack}>
      {guides.length > 0 && (
        <section className={styles.card} aria-labelledby="guides-heading">
          <h3 id="guides-heading" className={styles.cardTitle}>
            Guides
          </h3>
          <div className={styles.accordion}>
            {guides.map((g, i) => (
              <details key={`${g.title}-${i}`} className={styles.guide}>
                <summary className={styles.guideSummary}>{g.title}</summary>
                <p className={styles.guideBody}>{g.body}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {roadmap.length > 0 && (
        <section className={styles.card} aria-labelledby="roadmap-heading">
          <h3 id="roadmap-heading" className={styles.cardTitle}>
            Roadmap
          </h3>
          <ul className={styles.roadmap}>
            {roadmap.map((r, i) => (
              <li key={`${r.title}-${i}`} className={styles.roadmapRow}>
                <div className={styles.roadmapMain}>
                  <span className={styles.roadmapTitle}>{r.title}</span>
                  {r.description && <span className={styles.roadmapDesc}>{r.description}</span>}
                </div>
                {r.status && (
                  <span className={styles.badge} data-status={r.status.toLowerCase()}>
                    {r.status}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {comingSoon.length > 0 && (
        <section className={styles.card} aria-labelledby="coming-heading">
          <h3 id="coming-heading" className={styles.cardTitle}>
            Coming soon
          </h3>
          <ul className={styles.comingSoon}>
            {comingSoon.map((c, i) => (
              <li key={`${c.title}-${i}`} className={styles.comingSlot}>
                <span className={styles.comingTitle}>{c.title}</span>
                {c.description && <span className={styles.comingDesc}>{c.description}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
